console.log('[airtable/index.ts loaded]');

/**
 * Airtable Module Entry Point
 * 
 * Re-exports all components of the Airtable integration
 * for easy imports throughout the application
 */

// Core modules
export * from './config';
export * from './rest-api';
export * from './metadata-api';

// Import directly for local use
import { preloadTables } from './rest-api';

// Data & types
export * from './tables';
export * from './table-modules';
export * from './transform';

// Cache system
export {
  getCachedData,
  setCachedData,
  invalidateCache,
  invalidateCacheByPrefix,
  clearCache,
  getCacheStats,
  preloadCache,
  initializeCache,
  closeCache
} from './cache';

// Error handling
export * from './errors';
export * from './retry';

// Operations
export * from './batch';

// React hooks
export * from './hooks';

// Debug tools
export * as cacheDebug from './debug';

// Import specific types/functions needed for direct usage
import { 
  isAirtableError, 
  getUserFriendlyMessage 
} from './errors';

/**
 * Helper to check if Airtable integration is properly configured
 */
export async function isAirtableConfigured(): Promise<boolean> {
  const { airtableConfig } = await import('./config');
  return airtableConfig.isConfigValid();
}

/**
 * Helper to preload common tables into cache
 */
export async function preloadCommonTables(): Promise<void> {
  if (!await isAirtableConfigured()) {
    console.warn('Airtable not configured, skipping preload');
    return;
  }
  
  try {
    await preloadTables();
  } catch (error) {
    console.error('Error preloading tables:', error);
  }
}

// Re-export these to ensure they're available
export type { AirtableError, AirtableErrorType } from './types';
export type { AirtableRecord } from './tables';
export * from './errors';
export * from './retry';
